import { useGT } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import { useOpenDraftPurchaseOrders } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  openDraftLabel,
  pickAppendTarget,
  pickAwaitingApproval,
  pickDecidedDraft,
} from '../utils/openDraftOrders';

/**
 * Release's "Add to open PO" or "New PO" choice, shown when the cart's vendor ×
 * ship-to already has an unsent draft. Only an approved draft can take lines
 * (adding re-runs its approval); one still awaiting approval gets a note and
 * New PO is the only choice. `value` is the draft to add to, or null for a new
 * PO. Renders nothing when there is no open draft.
 */
export function ReleaseTargetChoice({
  supplierId,
  shipToWarehouseId,
  value,
  onChange,
}: {
  supplierId: string;
  shipToWarehouseId: string;
  value: string | null;
  onChange: (draftPurchaseOrderId: string | null) => void;
}) {
  const gt = useGT();
  const { data } = useOpenDraftPurchaseOrders({
    supplierId,
    shipToWarehouseId,
  });
  const orders = data?.orders ?? [];
  const target = pickAppendTarget(orders);
  const awaiting = pickAwaitingApproval(orders);
  const decided = target === null ? pickDecidedDraft(orders) : null;
  if (target === null && awaiting === null && decided === null) return null;
  return (
    <fieldset className="space-y-1 rounded border px-2 py-1.5 text-sm">
      <legend className="px-1 text-xs text-gray-500">
        {gt('This vendor and ship-to already has an unsent PO', {
          context: 'release dialog open purchase order legend',
        })}
      </legend>
      {target && (
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name="release-target"
            checked={value === target.id}
            onChange={() => onChange(target.id)}
          />
          <span>
            {gt('Add to open PO #{label} ({total})', {
              context: 'release dialog add to open purchase order',
              label: openDraftLabel(target),
              total: formatCurrency(target.totalAmount),
            })}
          </span>
        </label>
      )}
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="radio"
          name="release-target"
          checked={value === null}
          onChange={() => onChange(null)}
        />
        <span>
          {gt('New PO', { context: 'release dialog new purchase order' })}
        </span>
      </label>
      {awaiting && (
        <p className="text-xs text-gray-500">
          {gt('PO #{label} is awaiting approval, so lines cannot be added.', {
            context: 'release dialog awaiting approval note',
            label: openDraftLabel(awaiting),
          })}
        </p>
      )}
      {decided && (
        <p className="text-xs text-gray-500">
          {gt(
            'PO #{label} was already approved, so lines cannot be added without a new approval.',
            {
              context: 'release dialog already approved note',
              label: openDraftLabel(decided),
            },
          )}
        </p>
      )}
      {target && (
        <p className="text-xs text-gray-500">
          {gt('Adding lines sends the PO back through approval.', {
            context: 'release dialog append re-approval note',
          })}
        </p>
      )}
    </fieldset>
  );
}
